import React from 'react';
import { Link } from 'react-router';
import dollars from '../../dollar-format';
import { isNegotiationStatusFilter, peakifyListQuery } from '../../utils';

function integer(value) {
	if (value === undefined || value === null || value === '') return '';
	return Math.round(Number(value)).toLocaleString();
}

function percent(value) {
	if (value === undefined || value === null || value === '') return '';
	return Number(value).toFixed(2)+'%';
}

function roundedDollars(value){
	return dollars(Math.round(value))
}

function decimalsDollars(value){
	return dollars(value,true);
}

function hasStatusFilter(query) {
	if (!query) return false;
	return !!(query.STATUS || query.HSC_STATUS || query.SUMMIT_NEGUC || query.MAIN_CATEGORY === 'SUMMIT_NEGUC');
}

const SUMMIT_INVENTORY_LIST_URL = '/list?SUMMIT_INVENTORY=true&order=LISTED_DATE.desc';

function isSummitCurrentInventory(query) {
	if (!query) return false;
	if (query.SUMMIT_INVENTORY === 'true') return true;
	return query.SOURCE === 'SUMMIT' && query.STATUS === 'Active';
}

function AgentInfoCell({ agentName, productTypeName, territoryName, marketName, onAgentClick }) {
	if (!agentName) return null;
	return (
		<div
			className='ListStats_li'
			onClick={onAgentClick || undefined}
			style={{ cursor: onAgentClick ? 'pointer' : 'default', userSelect: 'none', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: 'auto', padding: '8px 30px', alignItems: 'flex-start' }}
		>
			<span className='ListStats_li_avg' style={{ whiteSpace: 'nowrap', marginBottom: '4px' }}>{agentName}</span>
			{productTypeName && <span className='ListStats_li_name'>{productTypeName}</span>}
			{marketName && <span className='ListStats_li_name'>{marketName}</span>}
			{territoryName && <span className='ListStats_li_name'>{territoryName}</span>}
		</div>
	);
}

function TerritoryStats({ query, newDevelopmentPropertyIds, hotListPropertyIds, needOwnerPropertyIds, investorHotListCount, stats, loading, territoryFlagCounts, ownerResearchReviews, activeBuyers, agentName, productTypeName, territoryName, marketName, onAgentClick }) {
	if (!stats && !loading) return null;

	// While a new list loads the labels stay and the values are left blank.
	const figures = stats || {};

	const totalSqft = figures.TOTAL_SQFT;
	const onMarketCount = Number(figures.ACTIVE_COUNT) || 0;

	// Prefer per-territory counts from the dedicated query when present;
	// fall back to the global ID sets (V7 / no-territory path).
	const hotListCount = territoryFlagCounts
		? territoryFlagCounts.hotList
		: (hotListPropertyIds || new Set()).size;
	const newDevCount = territoryFlagCounts
		? territoryFlagCounts.newDev
		: (newDevelopmentPropertyIds || new Set()).size;
	const needOwnerCount = territoryFlagCounts
		? territoryFlagCounts.needOwner
		: (needOwnerPropertyIds || new Set()).size;
	const researchedCount = territoryFlagCounts ? (territoryFlagCounts.researched || 0) : 0;
	const needInfoCount = territoryFlagCounts ? (territoryFlagCounts.needInfo || 0) : 0;

	// List Cap Avg: average cap rate of Active properties only, excluding 0/blank
	const activeCapSum = Number(figures.ACTIVE_CAP_NZ_SUM) || 0;
	const activeCapCnt = Number(figures.ACTIVE_CAP_NZ_CNT) || 0;
	const listCapAvg = activeCapCnt > 0 ? activeCapSum / activeCapCnt : null;

	// Closed in rolling last 12 months (matches the link's CLOSE_OF_ESCROW_MIN window)
	const closedCount = Number(figures.CLOSED_LAST_12M) || 0;
	const closedCapAvg = figures.CLOSED_CAP_LAST_12M != null && Number(figures.CLOSED_CAP_LAST_12M) > 0
		? Number(figures.CLOSED_CAP_LAST_12M) : null;

	const twelveMonthsAgo = new Date();
	twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

	// Build base query string from current filters (exclude STATUS-related params)
	const baseQuery = { ...query };
	delete baseQuery.STATUS;
	delete baseQuery.HSC_STATUS;
	delete baseQuery.CLOSE_OF_ESCROW_MIN;
	delete baseQuery.CLOSE_OF_ESCROW_MAX;
	delete baseQuery.NEW_DEVELOPMENT;
	delete baseQuery.HOT_LIST;
	delete baseQuery.HOT_LIST_USER_EMAIL;
	delete baseQuery.NEED_OWNER;
	delete baseQuery.RESEARCHED;
	delete baseQuery.NEED_INFO;
	delete baseQuery.page;

	const toQueryString = (params) => {
		const merged = peakifyListQuery({ ...baseQuery, ...params });
		return Object.keys(merged)
			.filter(k => merged[k] != null && merged[k] !== '')
			.map(k => `${encodeURIComponent(k)}=${encodeURIComponent(merged[k])}`)
			.join('&');
	};

	const today = new Date();
	const twelveMonthsAgoStr = `${(twelveMonthsAgo.getMonth() + 1).toString().padStart(2, '0')}/${twelveMonthsAgo.getDate().toString().padStart(2, '0')}/${twelveMonthsAgo.getFullYear()}`;

	// Determine which filter is currently active so we can highlight its box
	const activeKey = query.HOT_LIST === 'y' ? 'hot-list'
		: query.NEW_DEVELOPMENT === 'y' ? 'new-development'
		: query.NEED_OWNER === 'y' ? 'need-owner'
		: query.RESEARCHED === 'y' ? 'researched'
		: query.NEED_INFO === 'y' ? 'need-info'
		: query.STATUS === 'Active' ? 'on-market'
		: (query.STATUS === 'Closed' && query.CLOSE_OF_ESCROW_MIN) ? 'closed-12'
		: null;

	const columns = [
		{
			key: 'total-properties',
			label: '# of Properties',
			value: Number(figures.count).toLocaleString() ? Number(figures.count).toLocaleString() : '0',
			link: `/list?${toQueryString({})}`,
		},
		{
			key: 'total-sqft',
			label: 'Total Sq Ft',
			value: integer(totalSqft),
		},
		{
			key: 'need-info',
			label: 'Need Info',
			value: Number(needInfoCount).toLocaleString(),
			link: needInfoCount > 0 ? `/list?${toQueryString({ NEED_INFO: 'y' })}` : null,
		},
		{
			key: 'hot-list',
			label: 'Hot List',
			value: Number(hotListCount).toLocaleString(),
			// Hot List is per-agent: the link drops the territory filters so it
			// shows the user's full hot list.
			link: hotListCount > 0 ? '/list?HOT_LIST=y' : null,
		},
		{
			key: 'new-development',
			label: 'New Development',
			value: Number(newDevCount).toLocaleString(),
			link: newDevCount > 0 ? `/list?${toQueryString({ NEW_DEVELOPMENT: 'y' })}` : null,
		},
		{
			key: 'investor-hot-list',
			label: 'Investor Hot List',
			value: Number(investorHotListCount || 0).toLocaleString(),
			link: (investorHotListCount || 0) > 0 ? '/investors?HOT_LIST=y' : null,
		},
		{
			key: 'need-owner',
			label: 'Need Owner',
			value: Number(needOwnerCount).toLocaleString(),
			link: needOwnerCount > 0 ? `/list?${toQueryString({ NEED_OWNER: 'y' })}` : null,
		},
		{
			key: 'researched',
			label: 'Researched',
			value: Number(researchedCount).toLocaleString(),
			link: researchedCount > 0 ? `/list?${toQueryString({ RESEARCHED: 'y' })}` : null,
		},
		// The properties the owner research bot left for a person in this
		// territory; the client asked for them here, where the agent works.
		ownerResearchReviews && {
			key: 'owner-review',
			label: 'Owner Research Review',
			value: Number(ownerResearchReviews.count).toLocaleString(),
			link: ownerResearchReviews.count > 0 ? ownerResearchReviews.link : null,
		},
		// Owners of this territory's properties ticked Active Buyer in Kore.
		activeBuyers && {
			key: 'active-buyers',
			label: 'Active Buyers',
			value: activeBuyers.error ? '!' : Number(activeBuyers.count).toLocaleString(),
			title: activeBuyers.error,
			link: activeBuyers.count > 0 ? activeBuyers.link : null,
		},
		{
			key: 'on-market',
			label: 'On Market',
			value: Number(onMarketCount).toLocaleString(),
			link: onMarketCount > 0 ? `/list?${toQueryString({ STATUS: 'Active' })}` : null,
		},
		{
			key: 'list-cap-avg',
			label: 'List Cap Avg',
			value: listCapAvg != null ? percent(listCapAvg) : '-',
			link: onMarketCount > 0 ? `/list?${toQueryString({ STATUS: 'Active' })}` : null,
		},
		{
			key: 'closed-12',
			label: 'Closed – Last 12 Mo',
			value: Number(closedCount).toLocaleString(),
			link: closedCount > 0 ? `/list?${toQueryString({ STATUS: 'Closed', CLOSE_OF_ESCROW_MIN: twelveMonthsAgoStr })}` : null,
		},
		{
			key: 'closed-cap-avg',
			label: 'Closed Avg Cap',
			value: closedCapAvg != null ? percent(closedCapAvg) : '-',
			link: closedCount > 0 ? `/list?${toQueryString({ STATUS: 'Closed', CLOSE_OF_ESCROW_MIN: twelveMonthsAgoStr })}` : null,
		},
	].filter(Boolean);

	return (
		<div className='ListStats'>
			<AgentInfoCell agentName={agentName} productTypeName={productTypeName} territoryName={territoryName} marketName={marketName} onAgentClick={onAgentClick} />
			{columns.map(col => {
				const isActive = activeKey === col.key;
				const content = (
					<div className='ListStats_li' key={col.key} title={col.title}>
						<span className='ListStats_li_avg'>{loading ? '' : col.value}</span>
						<span className='ListStats_li_name'>{col.label}</span>
					</div>
				);
				return col.link && !loading ? (
					<Link key={col.key} to={col.link} className={isActive ? 'ListStats_li--active' : ''} style={{ color: 'inherit', textDecoration: 'none' }}>
						{content}
					</Link>
				) : content;
			})}
		</div>
	);
}

const ListStats = (props) => {
	const { stats, loading, query, variant, items, newDevelopmentPropertyIds, hotListPropertyIds, needOwnerPropertyIds, territoryFlagCounts, ownerResearchReviews, activeBuyers, investorHotListCount, agentName, productTypeName, territoryName, marketName, onAgentClick } = props;
    const isHSC = query && (query.SOURCE === 'HSC' || query.SOURCE === 'PEAK');
    const isClosed = (query && query.CLOSE_OF_ESCROW_MIN) || (query && query.CLOSE_OF_ESCROW_MAX) || (query && query.STATUS === 'Closed') || (query && query.HSC_STATUS === 'Closed');

	// Firms variant: only show count with "# of Firms" label
	if (variant === 'firms') {
		const count = (stats && stats.count != null) ? Number(stats.count).toLocaleString() : '';
		return stats ? (
			<div className='ListStats'>
				<div className='ListStats_li' key='firms-count'>
					<span className='ListStats_li_avg'>{count}</span>
					<span className='ListStats_li_name'># of Firms</span>
				</div>
			</div>
		) : null;
	}

	// When no Status filter is applied, show territory stats
	if (!hasStatusFilter(query) && !isHSC && (stats || loading)) {
		return <TerritoryStats items={items} query={query} loading={loading} newDevelopmentPropertyIds={newDevelopmentPropertyIds} hotListPropertyIds={hotListPropertyIds} needOwnerPropertyIds={needOwnerPropertyIds} investorHotListCount={investorHotListCount} territoryFlagCounts={territoryFlagCounts} ownerResearchReviews={ownerResearchReviews} activeBuyers={activeBuyers} stats={stats} agentName={agentName} productTypeName={productTypeName} territoryName={territoryName} marketName={marketName} onAgentClick={onAgentClick} />;
	}

	const isNegotiation = isNegotiationStatusFilter(query);

	let columns = [
		{
			key: 'no-key',
			label: '# of Properties',
			format: function() {
				try {
					return Number(stats.count).toLocaleString();
				} catch (err) {
					return '';
				}
			},
		},
		{
			key: 'total-price',
			label: 'Total Price',
			format: function() {
				let key;

				if (isNegotiation) {
					key = 'TOTAL_NEGOTIATION_PRICE';
				} else if (isClosed) {
					key = 'TOTAL_SOLD_PRICE';
				} else if (isHSC) {
                    key = 'TOTAL_HSC_PRICE';
                } else {
					key = 'TOTAL_LIST_PRICE';
				}

				return dollars(stats[key]);
			},
		},
		{
			key: 'TOTAL_SQFT',
			label: 'Total SQ FT',
			format: integer,
		},
	];

	if (isHSC) {
        columns.push({
            key: 'TOTAL_LOAN_AMOUNT',
            label: 'Total Loan Volume',
            format: dollars,
        }, {
        	key: 'LOAN_AMOUNT',
			label: 'Loan Amount - Ave',
			format: roundedDollars
		}, {
        	key: 'LTV',
			label: 'LTV – Ave',
			format: percent,
		}, {
        	key: 'INTEREST_RATE',
			label: 'Rate – Ave',
			format: percent,
		}, {
        	key: 'LENDERS',
			label: '# of Lenders',
			format: integer,
        });
	} else {
        if (isClosed) {
            columns.push({
                key: 'SOLD_PRICE',
                label: 'Price at Close – Ave',
                format: roundedDollars
            }, {
                key: 'CLOSE_CAP_RATE',
                label: 'Cap Rate at Close – Ave',
                format: percent,
            }, {
                key: 'NET_OPERATING_INCOME',
                label: 'NOI – Ave',
                format: roundedDollars
            });
        }

        if (!isNegotiation) {
            columns.push({
                key: 'LIST_PRICE',
                label: 'List Price – Ave',
                format: roundedDollars
            }, {
                key: 'CAPITALIZATION_RATE',
                label: 'List Cap Rate – Ave',
                format: percent,
            });
        }

        if (!isClosed) {
            columns.push({
                key: 'NET_OPERATING_INCOME',
                label: 'NOI – Ave',
                format: roundedDollars
            })
        }

        if (isNegotiation) {
            columns.push({
                key: 'NEGOTIATION_PRICE',
                label: 'Neg. Price – Ave',
                format: roundedDollars
			}, {
                key: 'NEGOTIATION_CAP_RATE',
                label: 'Neg. Cap Rate – Ave',
                format: percent,
            })
        }

        if (isClosed) {
            columns.push({
                key: 'CLOSE_PRICE_PER_SQFT',
                label: 'Close PSF – Ave',
				format: decimalsDollars
            })
        } else if (isNegotiation) {
            columns.push({
                key: 'NEGOTIATION_PRICE_PER_SQFT',
                label: 'Neg. PSF – Ave',
				format: decimalsDollars
            });
        } else {
            columns.push({
                key: 'PRICE_PER_SQFT',
                label: 'PSF – Ave',
				format: decimalsDollars
            });
        }

        columns.push({
            key: 'RENT_PER_SQFT',
            label: 'Rent / SQ FT – Ave',
			format: decimalsDollars
        });

        columns.push({
            key: 'SQFT',
            label: 'SQ FT – Ave',
            format: integer,
        }, {
            key: 'VACANCY',
            label: 'Occupancy – Ave',
            format: percent,
        });
    }

	return stats || loading ? (
		<div className='ListStats'>
			<AgentInfoCell agentName={agentName} productTypeName={productTypeName} territoryName={territoryName} marketName={marketName} onAgentClick={onAgentClick} />
			{
				columns.map(col => {
					const content = (
						<div className='ListStats_li' key={col.key}>
							<span className='ListStats_li_avg'>{loading ? '' : col.key === 'LENDERS' ? props.uniquePropCount : col.format(stats[col.key])}</span>
							<span className='ListStats_li_name'>{col.label}</span>
						</div>
					);
					if (col.key === 'no-key' && isSummitCurrentInventory(query) && !loading) {
						return (
							<Link
								key={col.key}
								to={SUMMIT_INVENTORY_LIST_URL}
								style={{ color: 'inherit', textDecoration: 'none' }}
							>
								{content}
							</Link>
						);
					}
					return content;
				})
			}
		</div>
	) : null
};

export default ListStats;
